import postService from '../services/posts';
import { createReactionThunk } from './reactionThunks';
import type { AppDispatch } from '../store';
import type { PostItem, PostsState, ReduxAction, VotePayload } from '../types/common';

/**
 * The explore feed.
 *
 * A slice of its own, not a reuse of `state.posts`: explore used to render the
 * home feed's data filtered by media type, which worked only because both
 * showed the same global list. They now hold different content — home leads
 * with your herds, explore leads with your topics — so sharing one slice
 * would mean whichever page loaded last decided what both displayed.
 */
const initialState: PostsState = { loading: false, error: null, results: [] };

const exploreReducer = (
  state: PostsState = initialState,
  action: ReduxAction<PostsState | VotePayload | string>
): PostsState => {
  switch (action.type) {
    case 'SET_EXPLORE_LOADING':
      return { ...state, loading: true, error: null };
    case 'SET_EXPLORE_ERROR':
      return { ...state, loading: false, error: action.payload as string };
    case 'SET_EXPLORE_POSTS':
      return { ...(action.payload as PostsState), loading: false, error: null };
    case 'LOAD_MORE_EXPLORE_POSTS':
      return {
        ...(action.payload as PostsState),
        loading: false,
        error: null,
        results: [...state.results, ...((action.payload as PostsState).results || [])],
      };
    case 'TOGGLE_EXPLORE_REACTION':
      return {
        ...state,
        results: state.results.map((r: PostItem) =>
          r.id !== (action.payload as VotePayload).id
            ? r
            : { ...r, ...((action.payload as VotePayload).data || {}) }
        ),
      };
    // Hiding or deleting a post must take it out of explore too, not just home.
    case 'DELETE_POST':
    case 'HIDE_POST':
      return {
        ...state,
        results: state.results.filter((r: PostItem) => r.id !== (action.payload as string)),
      };
    default:
      return state;
  }
};

export const fetchExplorePosts = () => {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_EXPLORE_LOADING' });
    try {
      const posts = await postService.getExplorePosts(10, 1);
      dispatch({ type: 'SET_EXPLORE_POSTS', payload: posts });
    } catch (error: unknown) {
      dispatch({
        type: 'SET_EXPLORE_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load posts',
      });
    }
  };
};

export const loadMoreExplorePosts = (page: number) => {
  return async (dispatch: AppDispatch) => {
    const posts = await postService.getExplorePosts(10, page);
    dispatch({ type: 'LOAD_MORE_EXPLORE_POSTS', payload: posts });
  };
};

export const toggleExploreReaction = createReactionThunk('TOGGLE_EXPLORE_REACTION');

export default exploreReducer;
